import { useEffect, useState } from "react";
import { submissionsService, type SubmissionDeleteSummary } from "@/api/submissions.service";

type UseDeleteSummaryResult = {
  data: SubmissionDeleteSummary | null;
  loading: boolean;
  error: Error | null;
};

/**
 * Fetches the whole-submission delete summary when the modal is open. Runs only
 * while `enabled` is true (modal open) and a submission id is present, so the
 * request does not fire for the always-mounted-but-closed modal. Cleans up on
 * unmount / id change to avoid setting state after the modal closes.
 */
export function useDeleteSummary(
  submissionId: string | null | undefined,
  enabled: boolean,
): UseDeleteSummaryResult {
  const [data, setData] = useState<SubmissionDeleteSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!enabled || !submissionId) {
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    submissionsService
      .getDeleteSummary(submissionId)
      .then(result => {
        if (!cancelled) setData(result);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err : new Error(String(err)));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [submissionId, enabled]);

  return { data, loading, error };
}
